import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import api from '../api/api';
import { CURRENCY } from '../config/currency';

interface PaymentDifferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentComplete: (success: boolean, message: string) => void;
  orderId: number;
  newTotal: number;
}

interface PaymentPreview {
  currentTotal: number;
  newTotal: number;
  difference: number;
  paymentAction: 'NO_CHANGE' | 'ADDITIONAL_PAYMENT' | 'REFUND';
  additionalAmount?: number;
  refundAmount?: number;
  message: string;
}

const PaymentDifferenceModal: React.FC<PaymentDifferenceModalProps> = ({
  isOpen,
  onClose,
  onPaymentComplete,
  orderId,
  newTotal
}) => {
  const { theme } = useTheme();
  const [preview, setPreview] = useState<PaymentPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('UPI');

  // Fetch payment preview when modal opens
  useEffect(() => {
    if (isOpen && orderId) {
      fetchPaymentPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId, newTotal]);

  const fetchPaymentPreview = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/payments/orders/${orderId}/modification-preview`, {
        newTotal: newTotal
      });

      setPreview(response.data);
    } catch (err) {
      console.error('Error fetching payment preview:', err);
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to calculate payment difference');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentProcess = async () => {
    if (!preview) return;

    setProcessing(true);
    setError(null);

    try {
      const response = await api.post(`/payments/orders/${orderId}/modification-payment`, {
        originalAmount: preview.currentTotal,
        newAmount: preview.newTotal,
        paymentMethod: paymentMethod,
        paymentData: {
          // Add payment gateway specific data here
          // For UPI, card details, etc.
        }
      });

      if (response.data.success) {
        onPaymentComplete(true, response.data.message);
        onClose();
      } else {
        setError(response.data.message || 'Payment processing failed');
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Payment processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmNoPayment = () => {
    // For no change or refund scenarios, we can proceed without additional payment
    onPaymentComplete(true, preview?.message || 'Order modification completed');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" style={{ backgroundColor: theme.components?.card?.background || '#FFFFFF' }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold" style={{ color: theme.text }}>
            Payment Required
          </h2>
          <button
            onClick={onClose}
            style={{ color: theme.gray?.[400] || '#9ca3af' }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: theme.warning || '#F59E0B' }}></div>
            <p style={{ color: theme.gray?.[600] || '#4b5563' }}>Calculating payment difference...</p>
          </div>
        )}

        {error && (
          <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: `${theme.danger}10`, border: `1px solid ${theme.danger}30` }}>
            <p className="text-sm" style={{ color: theme.danger }}>{error}</p>
          </div>
        )}

        {preview && !loading && (
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="rounded-lg p-4" style={{ backgroundColor: theme.background }}>
              <h3 className="font-semibold mb-3" style={{ color: theme.text }}>Order Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span style={{ color: theme.gray?.[600] || '#4b5563' }}>Original Total:</span>
                  <span style={{ color: theme.text }}>{CURRENCY.symbol}{preview.currentTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: theme.gray?.[600] || '#4b5563' }}>New Total:</span>
                  <span style={{ color: theme.text }}>{CURRENCY.symbol}{preview.newTotal.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2" style={{ borderColor: theme.components?.card?.border || '#E8E6E1' }}>
                  <div className="flex justify-between font-semibold">
                    <span style={{ color: theme.text }}>Difference:</span>
                    <span style={{ color: preview.difference >= 0 ? theme.danger : theme.success }}>
                      {preview.difference >= 0 ? '+' : ''}{CURRENCY.symbol}{preview.difference.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Action */}
            <div className="text-center">
              {preview.paymentAction === 'NO_CHANGE' && (
                <div className="space-y-4">
                  <div style={{ color: theme.success }}>
                    <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p style={{ color: theme.gray?.[600] || '#4b5563' }}>{preview.message}</p>
                  <button
                    onClick={handleConfirmNoPayment}
                    className="w-full py-3 rounded-lg transition-colors"
                    style={{ backgroundColor: theme.success, color: 'white' }}
                  >
                    Confirm Modification
                  </button>
                </div>
              )}

              {preview.paymentAction === 'ADDITIONAL_PAYMENT' && (
                <div className="space-y-4">
                  <div style={{ color: theme.warning || '#F59E0B' }}>
                    <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>
                      Additional Payment Required
                    </h3>
                    <p className="text-2xl font-bold mb-2" style={{ color: theme.warning || '#F59E0B' }}>
                      {CURRENCY.symbol}{preview.additionalAmount?.toFixed(2)}
                    </p>
                    <p className="text-sm" style={{ color: theme.gray?.[600] || '#4b5563' }}>{preview.message}</p>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="text-left">
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.gray?.[700] || '#374151' }}>
                      Payment Method
                    </label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{
                        backgroundColor: theme.components?.input?.background || '#F0EDE8',
                        border: `1px solid ${theme.components?.input?.border || '#E8E6E1'}`,
                        color: theme.text
                      }}
                    >
                      <option value="UPI">UPI</option>
                      <option value="CARD">Credit/Debit Card</option>
                      <option value="WALLET">Digital Wallet</option>
                      <option value="CASH">Cash</option>
                    </select>
                  </div>

                  <button
                    onClick={handlePaymentProcess}
                    disabled={processing}
                    className="w-full py-3 rounded-lg transition-colors flex items-center justify-center"
                    style={{
                      backgroundColor: processing ? (theme.gray?.[400] || '#9ca3af') : (theme.warning || '#F59E0B'),
                      color: 'white'
                    }}
                  >
                    {processing ? (
                      <>
                        <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      `Pay ${CURRENCY.symbol}${preview.additionalAmount?.toFixed(2)}`
                    )}
                  </button>
                </div>
              )}

              {preview.paymentAction === 'REFUND' && (
                <div className="space-y-4">
                  <div style={{ color: theme.accent }}>
                    <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>
                      Refund Will Be Processed
                    </h3>
                    <p className="text-2xl font-bold mb-2" style={{ color: theme.accent }}>
                      {CURRENCY.symbol}{preview.refundAmount?.toFixed(2)}
                    </p>
                    <p className="text-sm" style={{ color: theme.gray?.[600] || '#4b5563' }}>{preview.message}</p>
                    <p className="text-xs mt-2" style={{ color: theme.gray?.[500] || '#6b7280' }}>
                      Refund will be processed to your original payment method within 3-5 business days.
                    </p>
                  </div>

                  <button
                    onClick={handlePaymentProcess}
                    disabled={processing}
                    className="w-full py-3 rounded-lg transition-colors flex items-center justify-center"
                    style={{
                      backgroundColor: processing ? (theme.gray?.[400] || '#9ca3af') : theme.accent,
                      color: 'white'
                    }}
                  >
                    {processing ? (
                      <>
                        <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Confirm & Process Refund'
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Cancel Button */}
            <button
              onClick={onClose}
              className="w-full py-2 transition-colors"
              style={{ color: theme.gray?.[600] || '#4b5563' }}
            >
              Cancel Modification
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentDifferenceModal; 